/**
 * OS DOCUMENTOS do pedido, do lado do Hub — 18/09/2026 (PRD §43.15).
 *
 * O desenho aprovado em imagem: `docs/sistema-visual-2026/documentos-17-09/`
 * (h1 e h2). Três peças:
 *
 * - **`QuadroDeDocumentos`** — dentro de "Conferir o pedido": cada arquivo com o
 *   estado (em conferência · liberado · recusado) e o que fazer com ele.
 * - **`VerDocumento`** — as páginas da CÓPIA convertida; o original, só com
 *   confirmação.
 * - **`RecebidoDoCliente`** — no caso: os documentos dos pedidos que viraram o
 *   contrato dele.
 *
 * ⛔⛔ **O que se abre é a cópia convertida em imagem, nunca o arquivo que
 * chegou.** O original é o que o cliente mandou — é o que se guarda para a prova —
 * e baixá-lo pede confirmação, dizendo o que ele traz.
 *
 * ⛔ **Nome e descrição são palavra do CLIENTE**: a descrição sai entre aspas e
 * nunca sobe para maiúscula.
 */
import { useEffect, useState } from 'react';
import { apiFormulario, type DocumentoDoPedido } from '../lib/api.ts';
import { usarDialogo } from '../lib/dialogo.ts';
import { avisarErro } from './Avisos.tsx';
import { Confirmar } from './Confirmar.tsx';
import { Icone } from './Icone.tsx';
import '../estilos/documentos-do-pedido.css';

/** O porquê da recusa, dito ao Richard. ⚠️ O código vem do Hub (`lib/formulario/quarentena.ts`). */
export const POR_QUE_RECUSOU: Record<string, string> = {
  antivirus: 'a conferência automática apontou este arquivo',
  codigo: 'o PDF traz código (JavaScript) que roda ao abrir',
  programa: 'o PDF manda abrir um programa no computador',
  embutido: 'o PDF carrega outro arquivo escondido dentro',
  tipo: 'o conteúdo não é PDF, JPG nem PNG',
  grande: 'passa de 20 MB',
  vazio: 'o arquivo chegou vazio',
  adulterado: 'o arquivo chegou danificado — não foi possível abri-lo com segurança',
  nao_abriu: 'não foi possível convertê-lo — pode estar corrompido',
  tempo: 'a conversão não terminou a tempo',
  nao_conferido: 'a conferência automática não conseguiu examiná-lo',
  nao_chegou: 'o cliente anexou, mas o arquivo não chegou',
};

const ROTULO_DO_TIPO: Record<string, string> = { pdf: 'PDF', jpg: 'JPG', png: 'PNG' };

function tamanho(b: number): string {
  return b < 1048576 ? `${Math.max(1, Math.round(b / 1024))} KB` : `${(b / 1048576).toFixed(1).replace('.', ',')} MB`;
}
function hora(iso: string): string {
  return new Intl.DateTimeFormat('pt-BR', { timeZone: 'America/Sao_Paulo', hour: '2-digit', minute: '2-digit' }).format(new Date(iso)).replace(':', 'h');
}
function haQuanto(iso: string): string {
  const min = Math.max(0, Math.round((Date.now() - new Date(iso).getTime()) / 60_000));
  if (min < 1) return 'chegou agora';
  if (min < 60) return `chegou há ${min} minuto${min === 1 ? '' : 's'}`;
  const h = Math.round(min / 60);
  return `chegou há ${h} hora${h === 1 ? '' : 's'}`;
}
/** A descrição do cliente, entre aspas — é palavra dele, não da KAIRÓS. */
function oQueE(d: DocumentoDoPedido): string {
  return d.descricao ? `“${d.descricao}”` : 'sem descrição';
}

export function Estado({ d }: { d: DocumentoDoPedido }) {
  if (d.estado === 'liberado')
    return (
      <span className="hd-estado liberado">
        <Icone nome="check" tamanho={12} /> Liberado
      </span>
    );
  if (d.estado === 'recusado')
    return (
      <span className="hd-estado recusado">
        <Icone nome="cancelado" tamanho={12} /> Recusado
      </span>
    );
  return (
    <span className="hd-estado conferindo">
      <Icone nome="relogio" tamanho={12} /> Em conferência
    </span>
  );
}

function Linha({ d, aoAbrir }: { d: DocumentoDoPedido; aoAbrir: () => void }) {
  const [vendoPorQue, setVendoPorQue] = useState(false);
  const nota =
    d.estado === 'liberado'
      ? `conferido às ${hora(d.conferidoEm ?? d.criadoEm)}${d.nota === 'acao_ao_abrir' ? ' · o original tem ação ao abrir' : ''}`
      : d.estado === 'recusado'
        ? (POR_QUE_RECUSOU[d.motivo ?? ''] ?? 'foi barrado na conferência')
        : haQuanto(d.criadoEm);
  return (
    <div className={`hd-arquivo ${d.estado === 'liberado' ? 'liberado' : d.estado === 'recusado' ? 'recusado' : 'conferindo'}`}>
      <span className="hd-tipo">{ROTULO_DO_TIPO[d.tipo ?? ''] ?? '?'}</span>
      <span className="hd-nome">
        <b title={d.nome}>{d.nome}</b>
        <small>
          {tamanho(d.tamanho)} · {oQueE(d)}
        </small>
      </span>
      <Estado d={d} />
      <span className="hd-nota">{d.estado === 'recusado' && !vendoPorQue ? 'barrado na conferência' : nota}</span>
      <span className="hd-acao">
        {d.estado === 'liberado' ? (
          <button type="button" className="botao-secundario s-botao s-botao-p" onClick={aoAbrir}>
            <Icone nome="olho" tamanho={14} /> Abrir
          </button>
        ) : d.estado === 'recusado' ? (
          <button type="button" className="botao-texto s-botao s-botao-p" onClick={() => setVendoPorQue((v) => !v)} aria-expanded={vendoPorQue}>
            {vendoPorQue ? 'Ocultar' : 'Detalhes'}
          </button>
        ) : (
          <span className="hd-esperando">aguarde</span>
        )}
      </span>
    </div>
  );
}

function Rodape() {
  return (
    <footer className="hd-pe">
      <span className="hd-pe-icone">
        <Icone nome="cadeado" tamanho={16} />
      </span>
      <p>
        <b>Você abre a cópia convertida em imagem</b>, não o arquivo que chegou. O original fica guardado cifrado, e baixá-lo pede confirmação.{' '}
        <b>O recusado não entra no caso</b> — fica só o registro de que chegou e foi barrado.
      </p>
    </footer>
  );
}

/**
 * O quadro dentro de "Conferir o pedido". ⭐ Enquanto houver algo em
 * conferência, pergunta de novo a cada 10 segundos — a conferência roda sozinha
 * no Hub, e a tela acompanha sem botão.
 */
export function QuadroDeDocumentos({ pedidoId, iniciais }: { pedidoId: string; iniciais: DocumentoDoPedido[] }) {
  const [docs, setDocs] = useState(iniciais);
  const [vendo, setVendo] = useState<DocumentoDoPedido | null>(null);
  const esperando = docs.some((d) => d.estado === 'em_conferencia');

  useEffect(() => {
    if (!esperando) return;
    let vivo = true;
    const t = setInterval(() => {
      apiFormulario
        .pedido(pedidoId)
        .then((p) => vivo && setDocs(p.documentos))
        .catch(() => undefined);
    }, 10_000);
    return () => {
      vivo = false;
      clearInterval(t);
    };
  }, [pedidoId, esperando]);

  if (!docs.length) return null;
  return (
    <section className="cad-bloco hd-quadro" aria-label="Documentos anexados pelo cliente">
      <header>
        <h6>
          Documentos <span className="hd-conta">{docs.length}</span>
        </h6>
      </header>
      <p className="hd-apoio">O cliente anexou e descreveu cada um com as palavras dele. Nada abre antes de a conferência terminar.</p>
      <div className="hd-lista">
        {docs.map((d) => (
          <Linha key={d.id} d={d} aoAbrir={() => setVendo(d)} />
        ))}
      </div>
      <Rodape />
      {vendo && <VerDocumento documento={vendo} aoFechar={() => setVendo(null)} />}
    </section>
  );
}

/**
 * ⚠️ O nome que o arquivo ganha ao salvar: o do cliente, sem caminho nem
 * caractere de controle. ⛔ A faixa de controle vai em `\x00`, e nunca na forma
 * com `u` — escrita nessa forma, a sequência virou byte nulo DENTRO do arquivo ao
 * ser gravada (18/09/2026), a mesma armadilha da busca de 15/08 (`CLAUDE.md`).
 */
function nomeParaSalvar(d: DocumentoDoPedido): string {
  const limpo = d.nome.replace(/[\\/\x00-\x1f<>:"|?*]+/g, '_').trim().slice(0, 150);
  return limpo || `documento.${d.tipo ?? 'pdf'}`;
}

/**
 * As páginas da cópia convertida. O original baixa só com confirmação — e o
 * nome dele nunca vai em cabeçalho: o servidor manda `documento.pdf`, e é aqui
 * que o arquivo ganha o nome verdadeiro.
 */
export function VerDocumento({ documento: d, aoFechar }: { documento: DocumentoDoPedido; aoFechar: () => void }) {
  const [pagina, setPagina] = useState(1);
  const [confirmando, setConfirmando] = useState(false);
  const total = d.paginas ?? 1;
  usarDialogo(aoFechar);

  async function baixar() {
    try {
      const r = await fetch(`/api/formulario/documentos/${d.id}/original`, { credentials: 'same-origin' });
      if (!r.ok) throw new Error('Não consegui baixar o original agora.');
      const url = URL.createObjectURL(await r.blob());
      const a = document.createElement('a');
      a.href = url;
      a.download = nomeParaSalvar(d);
      document.body.appendChild(a);
      a.click();
      a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 10_000);
    } catch (e) {
      avisarErro(e);
    } finally {
      setConfirmando(false);
    }
  }

  return (
    <div className="cortina hd-cortina" onMouseDown={(e) => e.target === e.currentTarget && aoFechar()}>
      <div className="dialogo hd-visor" role="dialog" aria-modal="true" aria-label={`Documento ${d.nome}`}>
        <header className="hd-visor-topo">
          <div>
            <span className="s-sobrelinha">
              Cópia convertida · página {pagina} de {total}
            </span>
            {/* ⛔ Não é `.titulo-secao`: o título da casa sobe tudo para maiúscula
                (decisão da marca), e nome de arquivo é palavra do cliente, não título. */}
            <p className="hd-visor-nome" title={d.nome}>
              {d.nome}
            </p>
            <p className="hd-apoio">{oQueE(d)}</p>
          </div>
          <button type="button" className="imv-fechar" aria-label="Fechar" onClick={aoFechar}>
            <Icone nome="fechar" tamanho={16} />
          </button>
        </header>
        <div className="hd-visor-folha">
          <img src={`/api/formulario/documentos/${d.id}/pagina/${pagina}`} alt={`Página ${pagina} de ${d.nome}`} />
        </div>
        {d.totalDePaginas !== null && d.paginas !== null && d.totalDePaginas > d.paginas && (
          <p className="hd-aviso">
            <Icone nome="atencao" tamanho={14} /> O PDF tem {d.totalDePaginas} páginas; a cópia traz as {d.paginas} primeiras. O restante está no original.
          </p>
        )}
        <footer className="dialogo-acoes hd-visor-pe">
          <button type="button" className="botao-texto" onClick={() => setConfirmando(true)}>
            <Icone nome="baixar" tamanho={14} /> Baixar o original
          </button>
          <span className="imv-esp" />
          <button type="button" className="botao-secundario" disabled={pagina <= 1} onClick={() => setPagina((p) => p - 1)}>
            <Icone nome="voltar" tamanho={14} /> Anterior
          </button>
          <button type="button" className="botao-secundario" disabled={pagina >= total} onClick={() => setPagina((p) => p + 1)}>
            Próxima <Icone nome="avancar" tamanho={14} />
          </button>
        </footer>
      </div>
      {confirmando && (
        <Confirmar
          titulo="Baixar o original?"
          descricao={
            'É o arquivo exatamente como o cliente mandou — o que se guarda para a prova. A cópia que você está vendo é imagem e basta para ler.' +
            (d.nota === 'acao_ao_abrir' ? ' Atenção: este original tem uma ação que roda ao abrir (sem código). Abra-o só se precisar.' : '')
          }
          rotuloAcao="Baixar o original"
          aoFechar={() => setConfirmando(false)}
          aoConfirmar={() => void baixar()}
        />
      )}
    </div>
  );
}

/**
 * ⭐ "Recebido do cliente", no caso — os documentos dos pedidos que viraram o
 * contrato dele. ⚠️ Mora no pedido; o caso LÊ. Uma cópia no caso seria uma
 * segunda verdade sobre o mesmo arquivo.
 */
export function RecebidoDoCliente({ casoId, casoCodigo, aoFechar }: { casoId: string; casoCodigo: string; aoFechar: () => void }) {
  const [pedidos, setPedidos] = useState<Array<{ id: string; recebidoEm: string; documentos: DocumentoDoPedido[] }> | null>(null);
  const [vendo, setVendo] = useState<DocumentoDoPedido | null>(null);
  usarDialogo(aoFechar);

  useEffect(() => {
    let vivo = true;
    apiFormulario
      .documentosDoCaso(casoId)
      .then((r) => vivo && setPedidos(r.pedidos))
      .catch((e) => vivo && avisarErro(e));
    return () => {
      vivo = false;
    };
  }, [casoId]);

  const todos = pedidos?.flatMap((p) => p.documentos) ?? [];
  const liberados = todos.filter((d) => d.estado === 'liberado');
  const outros = todos.filter((d) => d.estado !== 'liberado');
  const dia = (iso: string) => new Intl.DateTimeFormat('pt-BR', { timeZone: 'America/Sao_Paulo' }).format(new Date(iso));
  const apoio = !pedidos
    ? 'Abrindo…'
    : `Veio pelo${pedidos.length > 1 ? 's' : ''} pedido${pedidos.length > 1 ? 's' : ''} de ${pedidos.map((p) => dia(p.recebidoEm)).join(', ')}. ${
        liberados.length === 1 ? 'Um arquivo entrou.' : `${liberados.length} arquivos entraram.`
      }`;

  return (
    <div className="cortina hd-cortina" onMouseDown={(e) => e.target === e.currentTarget && aoFechar()}>
      <div className="dialogo hd-recebido" role="dialog" aria-modal="true" aria-label="Recebido do cliente">
        <header className="hd-visor-topo">
          <div>
            <span className="s-sobrelinha">Caso {casoCodigo} · documentos</span>
            <h2 className="titulo-secao">Recebido do cliente</h2>
            <p className="hd-apoio">{apoio}</p>
          </div>
          <button type="button" className="imv-fechar" aria-label="Fechar" onClick={aoFechar}>
            <Icone nome="fechar" tamanho={16} />
          </button>
        </header>
        {liberados.length > 0 && (
          <div className="hd-galeria">
            {liberados.map((d) => (
              <button type="button" key={d.id} className="hd-pagina" onClick={() => setVendo(d)} aria-label={`Abrir ${d.nome}`}>
                <span className="hd-papel">
                  <img src={`/api/formulario/documentos/${d.id}/pagina/1`} alt="" loading="lazy" />
                </span>
                <span className="hd-legenda">
                  <b title={d.nome}>{d.nome}</b>
                  <small>
                    {d.paginas} página{d.paginas === 1 ? '' : 's'} · {oQueE(d)}
                  </small>
                </span>
              </button>
            ))}
          </div>
        )}
        {outros.length > 0 && (
          <div className="hd-lista hd-lista-outros">
            {outros.map((d) => (
              <Linha key={d.id} d={d} aoAbrir={() => setVendo(d)} />
            ))}
          </div>
        )}
        {pedidos && todos.length === 0 && <p className="vazio">Nenhum documento veio pelo pedido deste caso.</p>}
        <footer className="hd-pe">
          <span className="hd-pe-icone">
            <Icone nome="baixar" tamanho={16} />
          </span>
          <p>
            O que você vê é a <b>cópia convertida</b>. <b>Baixar o original</b> pede confirmação, porque ele é o arquivo como o cliente mandou — e é o
            que se guarda para a prova.
          </p>
        </footer>
      </div>
      {vendo && <VerDocumento documento={vendo} aoFechar={() => setVendo(null)} />}
    </div>
  );
}
